import React from "react";

/**
 * RelativeTime
 * -------------------------------------------------------
 * Renders a relative timestamp like "2h ago", "3d ago", "just now".
 * Shows the absolute ISO/locale date in a tooltip (title attribute).
 *
 * Props:
 *   - value: ISO date string, number (ms), or Date
 *   - fallback: what to render when value is missing/invalid (default "—")
 *   - className: passed to the <span>
 */
export default function RelativeTime({ value, fallback = "—", className }) {
  if (value == null || value === "") {
    return <span className={className}>{fallback}</span>;
  }
  const d = value instanceof Date ? value : new Date(value);
  if (isNaN(d.getTime())) {
    return <span className={className}>{fallback}</span>;
  }

  const diffMs = Date.now() - d.getTime();
  const label = formatRelative(diffMs);
  const absolute = d.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });

  return (
    <span className={className} title={absolute}>
      {label}
    </span>
  );
}

function formatRelative(diffMs) {
  const sec = Math.round(diffMs / 1000);
  if (sec < 0) {
    // future timestamp
    return "in the future";
  }
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mon = Math.round(day / 30);
  if (mon < 12) return `${mon}mo ago`;
  const yr = Math.round(mon / 12);
  return `${yr}y ago`;
}
